import React, { useMemo } from "react"
import { IconButton, Badge, CircularProgress, FormControlLabel } from "@mui/material"
import CachedIcon from "@mui/icons-material/Cached"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import NotificationsIcon from "@mui/icons-material/Notifications"

import { HomepageNavigationItems } from "../../config/nav"
import { Actions, useDispatch, useSelector } from "../../stores/dashboard.store"
import { stringHasContent } from '../../util/util'

import Logo from "../logo/logo"

import styles from "./styles.module.css"
import Button from "../nura/button/button"

const shouldRenderPage = (user, page) => {
    if (page?._atDev === true && user?.at !== 'dev') return false

    if (!page?._show) return false
    if (page?._requiresDeveloper === true && user?.is_developer === false) return false
    if (page?._requiresNotDeveloper === true && user?.is_developer === true) return false

    return true
}

const NavItem = ({ dashboardView, name, page, user, setActivePage }) => {
    if (!shouldRenderPage(user, page)) return null

    return (
        <div
            key={page.id}
            onClick={() => setActivePage(page)}
            className={
                dashboardView?.id === page?.id
                    ? styles.sidebarItemActive
                    : styles.sidebarItem
            }
        >
            <page.Icon />
            {page?._name || name}
        </div>
    )

}

function DesktopProjectsNav({
    Component,
    user,
    projectMenu,
    openNotificationsMenu,
    notificationsRefMenu,
    hideNotifications,
    activeNotificationsCount,
    accountMenuRef,
    navigateDashboard,
    navItems,
    dashboardView,
    setActivePage,
    hasUnfulfilledNodes,
    activeProjectRequiresSave,
    isTaskQueued,
    isTaskLoading,
    loadingIndeterminate,
}) {
    const dispatch = useDispatch()
    const { projects, activeProject } = useSelector()

    const countShownNavItems = useMemo(
        () => Object.values(navItems).filter((x) => x?._show)?.length,
        [navItems]
    )

    const currentProjectName = useMemo(() => projects?.find(x => x?.id === activeProject?.id)?.name, [activeProject, projects])
    const areNodesEmpty = useMemo(() => activeProject?.nodes?.length === 0 || activeProject?.nodes?.length === undefined, [activeProject?.nodes])

    const categoryGrouped = useMemo(() => {
        return Object.keys(navItems).reduce((a, c) => {
            const category = navItems[c]?._category
            if (!!navItems[c]?._category) {
                if (a[category] === undefined) a[category] = {
                    _category: category,
                }

                a[category] = {
                    ...a[category],
                    [c]: navItems[c],
                }
            } else {
                a[c] = navItems[c]
            }

            return a
        }, {})
    }, [navItems])

    return (
        <div className={styles.page}>
            <div className={styles.topBar}>
                <div className={styles.orgStructure} onClick={navigateDashboard}>
                    <Logo />
                </div>
                <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                    {navItems?._changeOrg && (
                        <div
                            style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                            ref={projectMenu.ref}
                            onClick={projectMenu.handleClick}
                        >
                            {
                                stringHasContent(currentProjectName) && (
                                    <>
                                        {currentProjectName}
                                        <ArrowDropDownIcon style={{ color: "var(--text-dim)" }} />
                                    </>
                                )
                            }
                        </div>
                    )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 25 }}>
                    {user?.at === "dev" && <div className={styles.badge}>Test Mode</div>}

                    <div ref={notificationsRefMenu?.ref} onClick={openNotificationsMenu}>
                        <IconButton>
                            <Badge
                                color="#bbb"
                                badgeContent={hideNotifications ? 0 : activeNotificationsCount}
                                sx={{
                                    "& .MuiBadge-badge": {
                                        color: "white",
                                        backgroundColor: "grey",
                                        zIndex: 0,
                                    },
                                }}
                            >
                                <NotificationsIcon style={{ color: "var(--text-dim)" }} />
                            </Badge>
                        </IconButton>
                    </div>

                    <div
                        className={styles.accountName}
                        ref={accountMenuRef?.ref}
                        onClick={() => accountMenuRef.setShow(true)}
                    >
                        <div style={{ display: "flex", alignItems: "center" }}>
                            {user?.name?.split(" ")[0]}
                        </div>
                        <ArrowDropDownIcon style={{ color: "var(--text-dim)" }} />
                    </div>
                </div>
            </div>

            <div className={styles.navBody}>
                <div
                    className={
                        countShownNavItems === 0 ? styles.emptyNavSidebar : styles.navSidebar
                    }
                >
                    {navItems?._showBack && (
                        <div
                            className={styles.backButton}
                            onClick={() =>
                                dispatch(Actions.NavigateDashboard, {
                                    nav: HomepageNavigationItems._id,
                                    view: HomepageNavigationItems.ProjectList,
                                })
                            }
                        >
                            <ArrowBackIcon />
                            <div>Back</div>
                        </div>
                    )}

                    <div className={styles.sidebarGroup}>
                        {Object.keys(categoryGrouped)
                            .filter((key) => !key.startsWith("_"))
                            .map((key) => {
                                const page = categoryGrouped[key]
                                if (!!page?._category) {
                                    if (!Object.keys(page)
                                        .filter((key) => !key.startsWith("_"))
                                        .map((key) => {
                                            const pageCat = page[key]

                                            return shouldRenderPage(user, pageCat)
                                        }).every(x => x)) {
                                        return null
                                    }

                                    return (
                                        <>
                                            <div className={styles.navCategory}>
                                                {page._category}
                                            </div>
                                            {Object.keys(page)
                                                .filter((key) => !key.startsWith("_"))
                                                .map((key) => {
                                                    const pageCat = page[key]

                                                    return <NavItem
                                                        page={pageCat}
                                                        user={user}
                                                        setActivePage={setActivePage}
                                                        name={key}
                                                        dashboardView={dashboardView}
                                                    />
                                                })}
                                        </>
                                    )
                                }

                                return <NavItem
                                    page={page}
                                    user={user}
                                    setActivePage={setActivePage}
                                    name={key}
                                    dashboardView={dashboardView}
                                />
                            })}
                    </div>

                    {navItems?._showRun && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                position: "absolute",
                                bottom: 39,
                                alignItems: "center",
                                width: "inherit",
                                gap: 2,
                            }}
                        >
                            <Button
                                className={styles.runButton}
                                disabled={
                                    hasUnfulfilledNodes ||
                                    activeProjectRequiresSave ||
                                    isTaskQueued ||
                                    isTaskLoading ||
                                    areNodesEmpty
                                }
                                onClick={() => {
                                    // Show run dialog
                                    dispatch(Actions.SetShowRunDialog, true)
                                }}
                            >
                                <div style={{ color: "var(--button-text)", textTransform: "none" }}>
                                    {isTaskLoading
                                        ? "Loading"
                                        : isTaskQueued
                                            ? "Queued"
                                            : "Run"}
                                </div>
                                {isTaskLoading ? (
                                    <CircularProgress size={20} />
                                ) : isTaskQueued ? (
                                    <CachedIcon
                                        fontSize="small"
                                        className={styles.spinningIcon}
                                        style={{ color: "white" }}
                                    />
                                ) : (
                                    <PlayArrowIcon fontSize="small" style={{ color: "var(--button-text)" }} />
                                )}
                            </Button>
                        </div>
                    )}
                </div>

                <div className={styles.pageContent}>
                    {!(
                        loadingIndeterminate?.isFullApp &&
                        loadingIndeterminate?.visible
                    ) && <Component />}
                </div>
            </div>
        </div>
    )
}

export default DesktopProjectsNav